import AutocompleteFormField from '@components/Common/AutocompleteFormField';
import { SectionCard } from '@components/Common/EntityWorkspace';
import ErrorMessage from '@components/Common/ErrorMessage';
import FormField from '@components/Common/FormField';
import { PageHero } from '@components/Common/Panel';
import { useToast } from '@components/Common/Toast/ToastProvider';
import { createPromoCodeMutation } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import ToggleField from '@components/Common/ToggleField';
import { Box, Button } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type PromoForm, emptyPromoForm, normalizeCode, promoFormErrors } from './promoCodes';
import { fewestOpenJobsHint, useServedCities } from './useServedCities';
import './index.scss';

/**
 * A new promo code, on the create-page frame the company and job forms use. Created inactive by
 * default, so the offer page can be checked before employers can redeem it.
 */
const PromoCodeCreate: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { cities, openJobs, loading: citiesLoading } = useServedCities();
  const [form, setForm] = useState<PromoForm>(emptyPromoForm);
  const [errors, setErrors] = useState<Partial<Record<keyof PromoForm, string>>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof PromoForm>(key: K, value: PromoForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const submit = async () => {
    const found = promoFormErrors(form, { requireCode: true });
    setErrors(found);
    if (Object.values(found).some(Boolean)) {
      setError('Fix the highlighted fields, then create the code.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetchGraphQLApiData(createPromoCodeMutation(form));
      const created = res?.data?.createPromoCode;
      showToast(`${created?.code ?? normalizeCode(form.code)} created`, 'success');
      navigate(created?._id ? `/promo-codes/${created._id}` : '/promo-codes');
    } catch (e: any) {
      // The backend's refusals are written for this screen ("A promo code called X already
      // exists", "These aren't served cities: ..."), so they are shown as they are.
      setError(String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '') || 'Could not create the code.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="cc-create">
      <PageHero
        kicker="New promo code"
        title="Give employers a free first job post in a city."
        text="One free post per employer and per company, for jobs located in the cities you pick. Credit-pack and Premium Hiring accounts cannot use it. Anywhere else, employers pay as usual."
        actions={(
          <>
            <Button variant="outlined" onClick={() => navigate('/promo-codes')} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={submit} disabled={saving}>
              {saving ? 'Creating…' : 'Create code'}
            </Button>
          </>
        )}
      />

      {error && <ErrorMessage message={error} />}

      <Box className="cc-create__cards">
        <SectionCard title="Code">
          <FormField
            required
            name="code"
            label="Code"
            value={form.code}
            placeholder="NEWCITY"
            onChange={(_n, v) => set('code', String(v ?? '').toUpperCase())}
            error={!!errors.code}
            helperText={errors.code ?? 'Letters, digits and hyphens. It is printed on the offer page and cannot be changed later.'}
          />
          <FormField
            required={false}
            name="description"
            label="Description"
            value={form.description}
            placeholder="Mumbai, Q4"
            onChange={(_n, v) => set('description', String(v ?? ''))}
            helperText="For staff only. Employers never see it."
          />
          <ToggleField
            name="active"
            label={form.active ? 'Employers can redeem it' : 'Switched off until you check the offer page'}
            checked={form.active}
            onChange={(checked) => set('active', checked)}
          />
        </SectionCard>

        <SectionCard title="Where it applies">
          <AutocompleteFormField
            required
            multiple
            name="targetCities"
            label="Target cities"
            loading={citiesLoading}
            options={cities}
            value={form.targetCities}
            onChange={(_e, value) => set('targetCities', Array.isArray(value) ? value : [])}
            error={!!errors.targetCities}
            helperText={errors.targetCities ?? fewestOpenJobsHint(openJobs) ?? 'Pick from your served cities.'}
          />
        </SectionCard>

        <SectionCard title="Limits">
          <FormField
            required={false}
            type="date"
            name="expiresAt"
            label="Expires on"
            value={form.expiresAt}
            onChange={(_n, v) => set('expiresAt', String(v ?? ''))}
            error={!!errors.expiresAt}
            helperText={errors.expiresAt ?? 'Leave empty for no expiry.'}
          />
          <FormField
            required={false}
            type="number"
            name="maxRedemptions"
            label="Max redemptions"
            value={form.maxRedemptions}
            onChange={(_n, v) => set('maxRedemptions', String(v ?? ''))}
            error={!!errors.maxRedemptions}
            helperText={errors.maxRedemptions ?? 'Leave empty for no limit.'}
          />
          <FormField
            required
            type="number"
            name="jobValidityDays"
            label="Free post stays open for (days)"
            value={form.jobValidityDays}
            onChange={(_n, v) => set('jobValidityDays', String(v ?? ''))}
            error={!!errors.jobValidityDays}
            helperText={errors.jobValidityDays ?? 'A paid single post stays open for 2 days.'}
          />
        </SectionCard>
      </Box>
    </Box>
  );
};

export default PromoCodeCreate;
